import { useEffect, useRef, useState } from "react";

/**
 * HeroAmbientVideo — subtle background video (perfumery operating) as premium watermark.
 * Auto-plays muted looped with heavy blur + gradient overlay so it never overpowers the copy.
 */
export default function HeroAmbientVideo({ src, poster, opacity = 0.30, blur = 0 }) {
  const [ready, setReady] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!src) return;
    const el = ref.current;
    if (!el) return;
    // Ensure browser-policy-safe autoplay (muted + inline)
    el.muted = true;
    el.playsInline = true;
    const tryPlay = () => el.play().catch(() => {});
    tryPlay();
    // Retry on first user gesture (in case initial autoplay was blocked)
    const onGesture = () => { tryPlay(); document.removeEventListener("pointerdown", onGesture); };
    document.addEventListener("pointerdown", onGesture, { once: true });
    return () => document.removeEventListener("pointerdown", onGesture);
  }, [src]);

  if (!src) return null;
  const url = src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL || ""}${src}`;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" aria-hidden="true">
      <video
        ref={ref}
        src={url}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onCanPlay={() => setReady(true)}
        onLoadedData={() => setReady(true)}
        className={`w-full h-full object-cover transition-opacity duration-1000 ${ready ? "opacity-100" : "opacity-0"}`}
        style={{ opacity, filter: blur ? `blur(${blur}px)` : undefined }}
        data-testid="hero-ambient-video"
      />
      {/* Soft ivory wash to keep copy legible */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#FAFAFA]/60 via-[#FAFAFA]/40 to-[#FAFAFA]/85" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#FAFAFA]/40 via-transparent to-transparent" />
    </div>
  );
}
